import {
  Box,
  FormControl,
  FormLabel,
  IconButton,
  TextField,
} from "@mui/material";
import React, { useState } from "react";
import Certification from "../../../../types/Certification";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { gray } from "../../../shared-theme/themePrimitives";
import CancelIcon from "@mui/icons-material/Cancel";
import { CreatableAutocomplete } from "../shared/CustomCreatableAutocomplete";
import Course from "../../../../types/Course";

interface CertificationSectionProps {
  index: number;
  certification: Certification;
  courses: Course[];
  onCreateNewCourse: (title: string) => Promise<Course>;
  handleCertification: (e: any, index: number) => void;
  removeCertification: (index: number) => void;
}

export const CertificationSection: React.FC<CertificationSectionProps> = ({
  index,
  certification,
  courses,
  onCreateNewCourse,
  handleCertification,
  removeCertification,
}) => {
  const [hoveredSection, setHoveredSection] = useState<number | null>(null);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: "2rem",
        alignItems: "center",
        position: "relative",
        border:
          index > 0 && hoveredSection === index ? `1px solid ${gray[400]}` : "",
        borderRadius: index > 0 && hoveredSection === index ? "8px" : "",
        padding: "0.5rem",
        marginTop: index > 0 ? "1rem" : "",
      }}
      onMouseEnter={() => setHoveredSection(index)}
      onMouseLeave={() => setHoveredSection(null)}
    >
      {index > 0 && hoveredSection === index && (
        <IconButton
          onClick={() => removeCertification(index)}
          sx={{
            position: "absolute",
            top: -10,
            right: -10,
            border: "none",
            backgroundColor: gray[100],
            "&:hover": {
              backgroundColor: gray[100],
            },
          }}
        >
          <CancelIcon />
        </IconButton>
      )}
      <CreatableAutocomplete
        options={courses}
        value={
          courses.find((c) => c.courseId === certification.courseId) ?? null
        }
        getLabel={(c) => c.course}
        getId={(c) => c.courseId}
        onCreate={onCreateNewCourse} // create course on backend
        onChange={(course) => {
          if (!course) {
            handleCertification(
              { target: { name: "course", value: "" } },
              index
            );
            return;
          }
          handleCertification(
            { target: { name: "course", value: course.courseId } },
            index
          );
        }}
        placeholder="Type or Select a course"
        title="Course"
      />
      <FormControl>
        <FormLabel htmlFor="place">Place</FormLabel>
        <TextField
          id={`place-${index}`}
          type="text"
          name="place"
          value={certification.place}
          required
          placeholder="Type a Place"
          onChange={(e) => handleCertification(e, index)}
          fullWidth
          variant="outlined"
          color={"primary"}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: "50px",
              boxSizing: "border-box",
            },
          }}
        />
      </FormControl>
      <FormControl>
        <FormLabel htmlFor="period">Period</FormLabel>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            disableFuture
            views={["year", "month"]}
            value={certification.date ? dayjs(certification.date) : null}
            sx={{
              "& .MuiOutlinedInput-root": {
                padding: "9px 15px",
                height: "50px",
              },
            }}
            onChange={(newValue) =>
              handleCertification(
                {
                  target: {
                    name: "date",
                    value:
                      newValue && dayjs(newValue).isValid()
                        ? newValue.toISOString()
                        : "",
                  },
                },
                index
              )
            }
          />
        </LocalizationProvider>
      </FormControl>
    </Box>
  );
};
